import { useEffect, useState, type ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Sunrise, Sun, Moon, CloudSun, CloudRain, CloudLightning, Zap, Save, Heart, Shield, Swords, Skull,
  Pickaxe, Palette, Compass, Eye, UserPlus, MessageSquare, MonitorPlay, Loader2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { serverStore } from '../../stores/server.store';
import { userStore } from '../../stores/user.store';
import { confirmStore } from '../../stores/confirm.store';
import { Input } from '../design/Input';
import ToggleSwitch from '../common/ToggleSwitch';
import { McTextComposer } from '../common/McTextComposer';
import { cn } from '@/utils/cn';
import { bedrockTellrawCommand, bedrockTitleCommands, tellrawCommand, titleCommands } from '@/utils/mc-text';
import { SteamQuickActions } from './SteamQuickActions';
import { can } from '@/utils/permissions';

// Ruhige, neutrale Knöpfe – Farbe nur im Symbol (wie bei den Steam-Schnellaktionen).
const btn = 'flex items-center justify-center gap-1.5 rounded-xl border border-line bg-white/[0.02] text-xs font-semibold text-fg transition-colors hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed';

const TIME_WEATHER = [
  { id: 'day', icon: Sunrise, tone: 'text-orange-300', label: 'Morgen', command: 'time set day', success: 'Es ist Morgen' },
  { id: 'noon', icon: Sun, tone: 'text-warn', label: 'Mittag', command: 'time set noon', success: 'Es ist Mittag' },
  { id: 'night', icon: Moon, tone: 'text-[#A5A6F6]', label: 'Nacht', command: 'time set night', success: 'Es ist Nacht' },
  { id: 'clear', icon: CloudSun, tone: 'text-sky-300', label: 'Klar', command: 'weather clear', success: 'Wetter: klar' },
  { id: 'rain', icon: CloudRain, tone: 'text-blue-400', label: 'Regen', command: 'weather rain', success: 'Wetter: Regen' },
  { id: 'thunder', icon: CloudLightning, tone: 'text-[#A5A6F6]', label: 'Gewitter', command: 'weather thunder', success: 'Wetter: Gewitter' },
];

const DIFFICULTY = [
  { id: 'peaceful', icon: Heart, tone: 'text-online', label: 'Friedlich' },
  { id: 'easy', icon: Shield, tone: 'text-sky-300', label: 'Leicht' },
  { id: 'normal', icon: Swords, tone: 'text-warn', label: 'Normal' },
  { id: 'hard', icon: Skull, tone: 'text-red-400', label: 'Schwer' },
];

const GAMEMODES = [
  { id: 'survival', icon: Pickaxe, tone: 'text-online', label: 'Überleben' },
  { id: 'creative', icon: Palette, tone: 'text-sky-300', label: 'Kreativ' },
  { id: 'adventure', icon: Compass, tone: 'text-warn', label: 'Abenteuer' },
  { id: 'spectator', icon: Eye, tone: 'text-[#A5A6F6]', label: 'Zuschauer' },
];

const Section = ({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) => (
  <div>
    <div className="flex items-center justify-between gap-3 mb-2 min-h-6">
      <span className="text-[10px] text-fg-muted font-bold uppercase tracking-wider">{title}</span>
      {aside}
    </div>
    {children}
  </div>
);

/**
 * Minecraft-Schnellaktionen (2026-09-15 überarbeitet): ruhige Knöpfe, echte aktuelle
 * Schwierigkeit/Whitelist aus server.properties, Spielmodus für alle nur mit Rückfrage,
 * Nachricht mit Farbpunkten statt Farbcode-Tabelle.
 */
export const QuickActions = observer(() => {
  const { activeServer } = serverStore;
  const { currentUser } = userStore;
  const [busy, setBusy] = useState<string | null>(null);
  const [props, setProps] = useState<Record<string, any> | null>(null);
  const [kind, setKind] = useState<'java' | 'bedrock' | null>(null);
  const [player, setPlayer] = useState('');
  const [msg, setMsg] = useState('');
  const [sender, setSender] = useState('Server');

  const id = activeServer?.id;
  const online = activeServer?.status === 'online';
  const isSteam = String(activeServer?.type || '').startsWith('steam');

  useEffect(() => {
    setProps(null);
    if (!id || !online || isSteam) return;
    let alive = true;
    serverStore.fetchProperties(id).then((r) => {
      if (alive && r) { setProps(r.values); setKind(r.kind); }
    });
    return () => { alive = false; };
  }, [id, online, isSteam]);

  const isDev = typeof window !== 'undefined' && window.location.port === '3002';
  if (!activeServer || (!online && !isDev)) return null;
  if (!can(currentUser, 'can_moderate_server')) return null;
  // Steam-Spiele (ARK): eigene, pro Spiel im Katalog definierte Aktionen.
  if (isSteam) return <SteamQuickActions />;

  const server = activeServer;
  const canEdit = can(currentUser, 'can_configure_server');
  const bedrock = kind === 'bedrock' || /bedrock/i.test(String(server.type || ''));

  const run = async (key: string, commands: string | string[], success: string) => {
    setBusy(key);
    let ok = true;
    let message = '';
    for (const c of Array.isArray(commands) ? commands : [commands]) {
      const res = await serverStore.executeConsoleCommand(server.id, c);
      if (!res.success) { ok = false; message = res.message || ''; break; }
    }
    if (ok) toast.success(success);
    else toast.error(message || 'Befehl fehlgeschlagen');
    setBusy(null);
    return ok;
  };

  /** Admins: in server.properties speichern (wirkt sofort und bleibt nach Neustart). Sonst nur der Befehl. */
  const setProperty = async (key: 'difficulty' | 'white-list', value: string | boolean, command: string, success: string) => {
    if (canEdit && kind === 'java') {
      setBusy(key);
      const r = await serverStore.saveProperties(server.id, { [key]: value });
      setBusy(null);
      if (!r) return;
      setProps(r.values);
      toast.success(r.applied.includes(key) ? success : `${success} – gespeichert, wirkt ab dem nächsten Neustart`);
      return;
    }
    if (await run(key, command, success)) setProps((p) => (p ? { ...p, [key]: value } : p));
  };

  const setGamemode = async (g: (typeof GAMEMODES)[number]) => {
    const ok = await confirmStore.confirm({
      title: `Alle Spieler: ${g.label}`,
      message: `Alle Spieler, die gerade online sind, werden in den Spielmodus „${g.label}" gesetzt.`,
      confirmText: g.label,
      variant: 'warning',
    });
    if (ok) run(`gm-${g.id}`, `gamemode ${g.id} @a`, `Alle Spieler: ${g.label}`);
  };

  const addPlayer = async () => {
    const name = player.trim();
    if (!/^[A-Za-z0-9_]{3,16}$/.test(name)) {
      toast.error('Ungültiger Spielername (3–16 Zeichen: Buchstaben, Zahlen, _)');
      return;
    }
    if (await run('wl-add', `whitelist add ${name}`, `${name} steht jetzt auf der Whitelist`)) setPlayer('');
  };

  const send = async (where: 'chat' | 'screen') => {
    if (!msg.trim()) return;
    const cmds = where === 'chat'
      ? (bedrock ? bedrockTellrawCommand(msg, sender) : tellrawCommand(msg, sender))
      : (bedrock ? bedrockTitleCommands(msg, sender) : titleCommands(msg, sender));
    const ok = await run(`msg-${where}`, cmds, where === 'chat' ? 'Nachricht im Chat gesendet' : 'Nachricht auf dem Bildschirm angezeigt');
    if (ok) setMsg('');
  };

  const spinner = (key: string, icon: ReactNode) => (busy === key ? <Loader2 size={17} className="animate-spin text-fg-muted" /> : icon);
  const whitelistKnown = typeof props?.['white-list'] === 'boolean';
  const whitelistOn = !!props?.['white-list'];

  return (
    <div className="bg-surface border border-line rounded-2xl overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-line text-sm font-medium text-fg">
        <Zap size={15} className="text-fg-muted" /> Schnellaktionen
        <span className="text-xs font-normal text-fg-muted hidden sm:inline">wirken sofort im Spiel</span>
      </div>

      <div className="p-5 space-y-5">
        <Section title="Tageszeit & Wetter">
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {TIME_WEATHER.map((a) => (
              <button key={a.id} onClick={() => run(a.id, a.command, a.success)} disabled={!!busy} title={`/${a.command}`} className={cn(btn, 'flex-col py-3')}>
                {spinner(a.id, <a.icon size={17} className={a.tone} />)}
                {a.label}
              </button>
            ))}
          </div>
        </Section>

        <Section title="Schwierigkeit">
          <div className="grid grid-cols-4 gap-2">
            {DIFFICULTY.map((d) => {
              const active = props?.difficulty === d.id;
              return (
                <button
                  key={d.id}
                  aria-pressed={active}
                  disabled={!!busy || active}
                  onClick={() => setProperty('difficulty', d.id, `difficulty ${d.id}`, `Schwierigkeit: ${d.label}`)}
                  title={active ? 'Ist gerade eingestellt' : `Auf „${d.label}" stellen`}
                  className={cn(btn, 'flex-col py-2.5', active && 'border-ember/50 bg-ember/10 disabled:opacity-100 disabled:cursor-default')}
                >
                  {spinner('difficulty', <d.icon size={16} className={d.tone} />)}
                  {d.label}
                </button>
              );
            })}
          </div>
        </Section>

        <Section title="Spielmodus für alle Spieler">
          <div className="grid grid-cols-4 gap-2">
            {GAMEMODES.map((g) => (
              <button key={g.id} onClick={() => setGamemode(g)} disabled={!!busy} title={`/gamemode ${g.id} @a`} className={cn(btn, 'flex-col py-2.5')}>
                {spinner(`gm-${g.id}`, <g.icon size={16} className={g.tone} />)}
                {g.label}
              </button>
            ))}
          </div>
        </Section>

        <Section
          title="Whitelist"
          aside={whitelistKnown ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-fg-muted">{whitelistOn ? 'an – nur eingetragene Spieler' : 'aus – jeder darf rein'}</span>
              <ToggleSwitch
                enabled={whitelistOn}
                onChange={(v) => { if (!busy) setProperty('white-list', v, `whitelist ${v ? 'on' : 'off'}`, v ? 'Whitelist eingeschaltet' : 'Whitelist ausgeschaltet'); }}
              />
            </div>
          ) : undefined}
        >
          <div className="flex gap-2">
            <div className="flex-1 min-w-0">
              <Input
                placeholder="Spielername hinzufügen …"
                value={player}
                onChange={(e) => setPlayer(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addPlayer()}
                icon={<UserPlus size={14} />}
              />
            </div>
            <button onClick={addPlayer} disabled={!player.trim() || !!busy} className={cn(btn, 'px-3.5 shrink-0')}>
              {spinner('wl-add', <UserPlus size={15} className="text-online" />)} Hinzufügen
            </button>
          </div>
        </Section>

        <Section
          title="Nachricht an alle Spieler"
          aside={(
            <label className="flex items-center gap-2 text-xs text-fg-muted">
              Absender
              <input
                value={sender}
                onChange={(e) => setSender(e.target.value.slice(0, 24))}
                placeholder="(keiner)"
                className="w-24 rounded-md bg-black/25 border border-line px-2 py-1 text-xs text-fg focus:outline-none focus:border-ember/60"
              />
            </label>
          )}
        >
          <McTextComposer
            value={msg}
            onChange={setMsg}
            placeholder="Nachricht eingeben …"
            onEnter={() => send('chat')}
            previewPrefix={sender.trim() ? `§6§l[${sender.trim()}] §r§e` : '§e'}
          />
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button onClick={() => send('chat')} disabled={!msg.trim() || !!busy} className={cn(btn, 'py-2.5')}>
              {spinner('msg-chat', <MessageSquare size={15} className="text-fg-muted" />)} Im Chat
            </button>
            <button onClick={() => send('screen')} disabled={!msg.trim() || !!busy} className={cn(btn, 'py-2.5')}>
              {spinner('msg-screen', <MonitorPlay size={15} className="text-ember" />)} Auf dem Bildschirm
            </button>
          </div>
        </Section>

        <button onClick={() => run('save', 'save-all', 'Welt gespeichert')} disabled={!!busy} className={cn(btn, 'w-full py-2.5')}>
          {spinner('save', <Save size={15} className="text-online" />)} Welt jetzt speichern
        </button>
      </div>
    </div>
  );
});
